/* global HTMLVideoElement, TimeoutId */
import { useCallback, useState, useRef } from 'react';
import { isShallowEqual } from 'app/utils/object';

export interface IVideoSize {
  readonly width: number;
  readonly height: number;
}

export type SizeChangeCallback = (videoSize: IVideoSize) => void;

interface IArgs {
  onVideoSizeChange?: SizeChangeCallback;
}

const useVideoSize = ({ onVideoSizeChange }: IArgs = {}): [IVideoSize, (node: HTMLVideoElement | null) => void] => {
  const [videoSize, onResize] = useState<IVideoSize>({ width: 0, height: 0 });
  const videoElement = useRef<HTMLVideoElement | null>(null);
  const sizeCheckIntervalId = useRef<TimeoutId | null>(null);
  const latestSize = useRef<IVideoSize | null>(null);

  const checkSize = useCallback(() => {
    if (!(videoElement.current instanceof HTMLVideoElement)) {
      return;
    }

    const newSize: IVideoSize = {
      width:  videoElement.current.videoWidth,
      height: videoElement.current.videoHeight,
    };

    if (!isShallowEqual(latestSize.current, newSize)) {
      latestSize.current = newSize;
      onResize(newSize);
      onVideoSizeChange?.(newSize);
    }
  }, [onResize, onVideoSizeChange]);

  const videoRef = useCallback((element: HTMLVideoElement | null) => {
    if (sizeCheckIntervalId.current !== null) {
      clearInterval(sizeCheckIntervalId.current);
    }

    if (videoElement.current) {
      videoElement.current.removeEventListener('resize', checkSize);
      latestSize.current = null;
      onVideoSizeChange?.({ width: 0, height: 0 });
    }

    videoElement.current = element;

    if (videoElement.current) {
      videoElement.current.addEventListener('resize', checkSize);
      sizeCheckIntervalId.current = setInterval(checkSize, 1000);
    }
  }, [checkSize, onVideoSizeChange]);

  return [videoSize, videoRef];
};

export default useVideoSize;
